import type { TaskStatus } from '@/domains/task/enums';
import type { Event } from '@patsnap/tahiti-core';
import type { SupportedTargetLanguage } from '../enums';

export interface ContentItem {
    original: string;
    translation: string;
    graphIndex: number;
}

/**
 * 翻译内容类型
 */
export enum TranslateContentType {
    /**
     * 翻译结果
     */
    TranslationResult = 'translation_result',
}


/**
 * 翻译步骤
 */
export enum TranslateStep {
    /**
     * 初始状态
     * @description 初始状态, 提交任务前
     */
    StepInitial = '',
    /**
     * 等待异步结果中
     * @description 提交异步任务后, 异步任务结束前
     */
    StepWaiting = 'WAITING',
    /**
     * 翻译结果
     * @description 异步任务结束后或提交翻译任务后
     */
    StepResult = 'RESULT',
    /**
     * 预览结果
     */
    StepPreview = 'step_preview',
}

/**
 * 翻译任务服务端状态
 */
export interface TranslateServerSideState {
    /**
     * 任务状态
     * @description 任务状态: Ready 待开始, Running 进行中, Complete 已完成, Failed 失败, Interrupt 中断
     */
    taskStatus: TaskStatus,
    /**
     * 当前步骤
     */
    currentStep: TranslateStep,
    /**
     * 步骤详情
     */
    stepDetail?: Record<string, {
        contentType: TranslateContentType,
        hide?: boolean,
    }>,
    /**
     * 任务创建时间
     */
    createdAt: string,
    /**
     * 任务更新时间
     */
    updatedAt: string,
}

/**
 * 翻译任务选项
 */
export interface TranslateOptions {
    /**
     * 翻译文本
     */
    text: string;
    /**
     * 目标语言
     */
    targetLanguage: string;
    /**
     * 源语言
     */
    sourceLanguage?: string;
}

export enum SseState {
    /**
     * 初始状态
     */
    Initial = 'initial',
    /**
     * 等待初次输出
     */
    WaitingFirstOutput = 'waiting_first_output',
    /**
     * 输出中
     */
    Outputting = 'outputting',
    /**
     * 输出完成
     */
    OutputCompleted = 'output_completed',
    /**
     * 输出失败
     */
    OutputFailed = 'output_failed',
    /**
     * 输出被中断
     */
    OutputInterrupted = 'output_interrupted',
}

/**
 * SSE 生成器
 */
export interface SseGenerator {
    /**
     * 中止
     */
    abort: () => void;
    /**
     * 状态变化事件
     */
    onStateChange: Event<SseState>;
    /**
     * 重试
     */
    retry: () => void;
}

/**
 * 翻译加载状态
 */
export interface TranslateLoadingState {
    /**
     * 是否正在获取翻译结果中
     */
    isLoadingTranslateResult: boolean,
    /**
     * 是否正在重新生成翻译段落中
     */
    isLoadingRegenerateTranslateFragment: boolean,
    /**
     * 当前正在翻译的段落索引
     */
    currentTranslateFragmentIndex: number,
    /**
     * 是否等待首次响应中
     */
    waitingFirstResponse: boolean,
}

/**
 * 翻译状态
 */
export interface TranslateState extends TranslateLoadingState {
    title: string;
    result: ContentItem[];
    /**
     * 更新时间
     */
    updatedAt?: string;
    /**
     * 正在编辑的原文索引
     */
    editingOriginalIdx: number,
    /**
     * 正在编辑的译文索引
     */
    editingTranslationIdx: number,
}

/**
 * 描述翻译的原始输入，包括输入文本、目标语言和状态。
 */
export interface OriginalInput {
    input: string;
    sourceLang: SupportedTargetLanguage | null;
    targetLang: SupportedTargetLanguage | null;
    isSupportLang: boolean;
    /**
     * 是否正在检测语言中
     */
    isDetectingLanguage: boolean;
}

export type OriginalInputState = OriginalInput;

/**
 * 描述单个AI建议项，包括原文、翻译文和置信度百分比。
 */
export interface SuggestionItem {
    original: string[];
    translation: string;
    percentage: number;
    /**
     * 是否 AI 推荐
     */
    aiRecommend: boolean;
}

/**
 * 表示AI建议的状态，包括加载状态和SuggestionItem数组。
 */
export interface SuggestionState {
    isLoading: boolean;
    techTopic: string;
    suggestions: SuggestionItem[];
    activeSuggestionIdx: number;
}
